import React, { memo, useCallback, useMemo, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy16Regular, ChevronDown16Regular, ChevronUp16Regular } from '@fluentui/react-icons';
import {
    CODE_COLLAPSE_LINE_THRESHOLD,
    copyTextToClipboard,
    normalizeCodeLanguage,
} from './markdownUtils';
import styles from './richMarkdown.module.css';

function CodeBlock({ language, code, streaming = false, fenceComplete = true, streamingComplete = true }) {
    const normalizedLanguage = normalizeCodeLanguage(language);
    const lines = useMemo(() => String(code || '').split('\n'), [code]);
    const isLarge = lines.length > CODE_COLLAPSE_LINE_THRESHOLD;
    const [expanded, setExpanded] = useState(!isLarge);
    const [copied, setCopied] = useState(false);
    const shouldHighlight = !streaming || (fenceComplete && streamingComplete);

    const displayCode = useMemo(() => {
        if (!isLarge || expanded) return String(code || '');
        return lines.slice(0, CODE_COLLAPSE_LINE_THRESHOLD).join('\n');
    }, [code, expanded, isLarge, lines]);

    const handleCopy = useCallback(async () => {
        const ok = await copyTextToClipboard(code);
        if (!ok) return;
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    }, [code]);

    const label = normalizedLanguage === 'text' ? 'code' : normalizedLanguage;
    const showCopy = shouldHighlight;

    return (
        <div className={styles.codeBlock}>
            <div className={styles.codeBlockHeader}>
                <span className={styles.codeBlockLanguage}>{label}</span>
                <div className={styles.codeBlockActions}>
                    {shouldHighlight && isLarge ? (
                        <button
                            type="button"
                            className={styles.codeBlockAction}
                            onClick={() => setExpanded(prev => !prev)}
                            aria-expanded={expanded}
                            aria-label={expanded ? 'Collapse code block' : 'Expand code block'}>
                            {expanded ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                            <span>{expanded ? 'Collapse' : 'Expand'}</span>
                        </button>
                    ) : null}
                    {showCopy ? (
                        <button
                            type="button"
                            className={styles.codeBlockAction}
                            onClick={handleCopy}
                            aria-label="Copy code to clipboard">
                            <Copy16Regular />
                            <span>{copied ? 'Copied' : 'Copy'}</span>
                        </button>
                    ) : null}
                </div>
            </div>
            <div className={styles.codeBlockBody} tabIndex={0} role="region" aria-label={`${label} code block`}>
                {shouldHighlight ? (
                    <SyntaxHighlighter
                        language={normalizedLanguage}
                        style={oneLight}
                        PreTag="div"
                        CodeTag="code"
                        customStyle={{ margin: 0, background: 'transparent', padding: 0 }}
                        codeTagProps={{ className: styles.syntaxCode }}>
                        {displayCode}
                    </SyntaxHighlighter>
                ) : (
                    <pre className={styles.plainCodePre}>
                        <code className={styles.syntaxCode}>{displayCode}</code>
                    </pre>
                )}
            </div>
            {shouldHighlight && isLarge && !expanded ? (
                <div className={styles.codeBlockTruncatedHint}>
                    Showing first {CODE_COLLAPSE_LINE_THRESHOLD} of {lines.length} lines
                </div>
            ) : null}
        </div>
    );
}

export default memo(CodeBlock);
